import React, { useCallback, useMemo, useState, useEffect } from 'react'
import moment from 'moment'
import styles from './IntervalSelect.module.scss'
import { DatePicker, Radio, RadioChangeEvent } from 'antd'
import {
  IconAngleLeft,
  IconAngleRight,
  IconAnglesLeft,
  IconAnglesRight,
  IconCalendar,
  IconCaretRightCircle,
} from '@utils/icons'

const { RangePicker } = DatePicker

interface Props {
  selectedDate: [moment.Moment | null, moment.Moment | null]
  setSelectedDate: React.Dispatch<
    React.SetStateAction<[moment.Moment | null, moment.Moment | null]>
  >
  showDays?: boolean
  showWeeks?: boolean
  showMonth?: boolean
}

type PresetKey =
  | 'today'
  | 'yesterday'
  | 'thisWeek'
  | 'lastWeek'
  | 'thisMonth'
  | 'lastMonth'
  | 'custom'

const IntervalSelect: React.FC<Props> = ({
  setSelectedDate,
  selectedDate,
  showDays = true,
  showWeeks = true,
  showMonth = false,
}) => {
  const [active, setActive] = useState<PresetKey>('today')

  const presets = useMemo(() => {
    const now = moment()
    return {
      today: [now.clone().startOf('day'), now.clone().endOf('day')] as [
        moment.Moment,
        moment.Moment,
      ],
      yesterday: [
        now.clone().subtract(1, 'day').startOf('day'),
        now.clone().subtract(1, 'day').endOf('day'),
      ] as [moment.Moment, moment.Moment],
      thisWeek: [now.clone().startOf('week'), now.clone().endOf('day')] as [
        moment.Moment,
        moment.Moment,
      ],
      lastWeek: [
        now.clone().subtract(1, 'week').startOf('week'),
        now.clone().subtract(1, 'week').endOf('week'),
      ] as [moment.Moment, moment.Moment],
      thisMonth: [now.clone().startOf('month'), now.clone().endOf('month')] as [
        moment.Moment,
        moment.Moment,
      ],
      lastMonth: [
        now.clone().subtract(1, 'month').startOf('month'),
        now.clone().subtract(1, 'month').endOf('month'),
      ] as [moment.Moment, moment.Moment],
    }
  }, [])

  const options = useMemo(() => {
    const list: { label: string; value: PresetKey }[] = []

    if (showDays) {
      list.push({ label: 'Сегодня', value: 'today' })
      list.push({ label: 'Вчера', value: 'yesterday' })
    }
    if (showWeeks) {
      list.push({ label: 'Текущая неделя', value: 'thisWeek' })
      list.push({ label: 'Прошлая неделя', value: 'lastWeek' })
    }
    if (showMonth) {
      list.push({ label: 'Текущий месяц', value: 'thisMonth' })
      list.push({ label: 'Прошлый месяц', value: 'lastMonth' })
    }

    list.push({ label: 'Произвольный период', value: 'custom' })
    return list
  }, [showDays, showWeeks, showMonth])

  useEffect(() => {
    if (!options.some((o) => o.value === active)) {
      const first = options[0]?.value ?? 'custom'
      setActive(first)
      if (first !== 'custom') {
        const range = presets[first]
        if (range) setSelectedDate([range[0], range[1]])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options])

  const applyRange = useCallback(
    (start: moment.Moment, end: moment.Moment) => {
      setSelectedDate([start, end])
    },
    [setSelectedDate],
  )

  const onPresetChange = useCallback(
    (e: RadioChangeEvent) => {
      const key = e.target.value as PresetKey
      setActive(key)

      if (key === 'custom') return

      const range = presets[key]
      if (range) applyRange(range[0], range[1])
    },
    [applyRange, presets],
  )

  const onPickerChange = useCallback(
    (values: [moment.Moment | null, moment.Moment | null] | null) => {
      setActive('custom')
      if (values?.[0] && values?.[1]) {
        setSelectedDate([values[0].clone().startOf('day'), values[1].clone().endOf('day')])
      }
    },
    [setSelectedDate],
  )

  const isCustom = active === 'custom'

  return (
    <div className={styles.wrapper}>
      <Radio.Group
        className={styles.segmented}
        value={active}
        onChange={onPresetChange}
        buttonStyle='solid'
      >
        {options.map((o) => (
          <Radio.Button key={o.value} value={o.value} className={styles.segmentedItem}>
            {o.label}
          </Radio.Button>
        ))}
      </Radio.Group>

      <div>
        <RangePicker
          getPopupContainer={() => document.getElementById('App') || document.body}
          value={selectedDate}
          onChange={onPickerChange}
          disabled={!isCustom}
          showTime={{ format: 'HH:mm' }}
          format='YYYY-MM-DD HH:mm'
          className={styles.range}
          suffixIcon={<IconCalendar width='1rem' height='1rem' fill='var(--color-arrows-date)' />}
          nextIcon={<IconAngleRight width='18' height='18' fill='var(--color-arrows-date)' />}
          prevIcon={<IconAngleLeft width='18' height='18' fill='var(--color-arrows-date)' />}
          superNextIcon={<IconAnglesRight width='18' height='18' fill='var(--color-arrows-date)' />}
          superPrevIcon={<IconAnglesLeft width='18' height='18' fill='var(--color-arrows-date)' />}
          separator={<IconCaretRightCircle width='18' height='18' fill='var(--color-main)' />}
        />
      </div>
    </div>
  )
}

export default IntervalSelect
